import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import ConfidentialNotice from "@/components/ConfidentialNotice";
import ProfilePicker from "@/components/ProfilePicker";
import BrandIntro from "@/components/BrandIntro";
import NotificationBell from "@/components/NotificationBell";
import LanguageToggle from "@/components/LanguageToggle";
import {
  Home, MessageSquare, Search, Sparkles, Swords, BookOpen,
  Users, User, Crown, Shield, LogOut, GraduationCap, History as HistoryIcon, LayoutDashboard,
  Menu, X, Scale, Newspaper, Mail, Landmark, MessageSquareHeart, Megaphone, Lightbulb, Quote, Map, Users2, LifeBuoy, Mic, Compass,
} from "lucide-react";

const NAV = [
  { to: "/", k: "nav.home", icon: Home },
  { to: "/citation", k: "nav.citation", icon: Quote },
  { to: "/situation", k: "nav.situation", icon: LifeBuoy },
  { to: "/reply", k: "nav.reply", icon: MessageSquare },
  { to: "/analyze", k: "nav.analyze", icon: Search },
  { to: "/create", k: "nav.create", icon: Sparkles },
  { to: "/debate", k: "nav.debate", icon: Swords },
  { to: "/coach", k: "nav.coach", icon: GraduationCap },
  { to: "/carte-afro", k: "nav.afromap", icon: Map },
  { to: "/espace-communautaire", k: "nav.communitySpace", icon: Users2 },
  { to: "/jeunes", k: "nav.youth", emoji: "👦" },
  { to: "/parents", k: "nav.parents", emoji: "👨‍👩‍👧" },
  { to: "/rights", k: "nav.rights", icon: Scale },
  { to: "/courriers", k: "nav.letters", icon: Mail },
  { to: "/actualites", k: "nav.news", icon: Newspaper },
  { to: "/library", k: "nav.library", icon: BookOpen },
  { to: "/auteurs", k: "nav.authors", icon: Landmark },
  { to: "/bibliotheque", k: "nav.bookshelf", emoji: "📚" },
  { to: "/partenaires", k: "nav.professionals", emoji: "🤝🏿" },
  { to: "/community", k: "nav.community", icon: Users },
  { to: "/idees", k: "nav.ideas", icon: Lightbulb },
  { to: "/nouveautes", k: "nav.whatsnew", icon: Megaphone },
  { to: "/votre-avis", k: "nav.feedback", icon: MessageSquareHeart },
  { to: "/history", k: "nav.history", icon: HistoryIcon },
  { to: "/dashboard", k: "nav.dashboard", icon: LayoutDashboard },
  { to: "/apropos", k: "nav.about", icon: Landmark },
];

const Glyph = ({ icon: Icon, emoji, active }) =>
  emoji ? (
    <span className="text-lg leading-none w-[18px] text-center">{emoji}</span>
  ) : (
    <Icon size={18} strokeWidth={1.5} className={active ? "text-gold" : ""} />
  );

// Barres de navigation mobiles dédiées par univers (le centre est un gros micro).
const PROFILE_BOTTOM_NAV = {
  child: {
    left: [{ to: "/", k: "nav.home", icon: Home }, { to: "/citation", k: "nav.discover", icon: Compass }],
    right: [{ to: "/situation", k: "nav.help", icon: LifeBuoy }, { to: "/profile", k: "nav.me", icon: User }],
    mic: "/?raconte=1",
  },
  teen: {
    left: [{ to: "/", k: "nav.home", icon: Home }, { to: "/reply", k: "nav.reply", icon: MessageSquare }],
    right: [{ to: "/rights", k: "nav.rightsShort", icon: Scale }, { to: "/profile", k: "nav.profileShort", icon: User }],
    mic: "/?raconte=1",
  },
  parent: {
    left: [{ to: "/", k: "nav.home", icon: Home }, { to: "/situation", k: "nav.help", icon: LifeBuoy }],
    right: [{ to: "/bibliotheque", k: "nav.resources", icon: BookOpen }, { to: "/profile", k: "nav.me", icon: User }],
    mic: "/situation",
  },
};

function ProfileBottomNav({ profile, location }) {
  const { t } = useTranslation();
  const cfg = PROFILE_BOTTOM_NAV[profile];
  const Item = ({ to, k, icon: Icon }) => {
    const active = location.pathname === to;
    return (
      <Link to={to} data-testid={`mnav-${to === "/" ? "home" : to.slice(1)}`}
        className="flex flex-col items-center gap-0.5 py-1 text-[10px]"
        style={{ color: active ? "var(--accent)" : "color-mix(in srgb, var(--text-main) 55%, transparent)" }}>
        <Icon size={20} strokeWidth={1.7} /> {t(k)}
      </Link>
    );
  };
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t grid grid-cols-5 items-end px-1 py-1.5"
      style={{ borderColor: "var(--surface-border)" }} data-testid={`bottom-nav-${profile}`}>
      {cfg.left.map((n) => <Item key={n.to} {...n} />)}
      <div className="flex justify-center">
        <Link to={cfg.mic} data-testid="mnav-mic"
          className="-mt-7 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-200 active:scale-90"
          style={{ background: "var(--accent-gradient)", boxShadow: "0 8px 24px -6px color-mix(in srgb, var(--accent) 60%, transparent)" }}>
          <Mic size={24} color="var(--accent-fg)" />
        </Link>
      </div>
      {cfg.right.map((n) => <Item key={n.to} {...n} />)}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const profile = user?.profile;
  const themed = profile === "child" || profile === "teen" || profile === "parent";
  const location = useLocation();
  const navigate = useNavigate();
  // Garde de sécurité mineurs : un compte enfant/ado ne peut pas accéder aux espaces sociaux/publics.
  const isMinorUser = user?.is_minor || profile === "child" || profile === "teen";
  useEffect(() => {
    if (!isMinorUser) return;
    const p = location.pathname;
    if (p.startsWith("/community") || p.startsWith("/espace-communautaire") || p.startsWith("/membre") || p.startsWith("/idees")) {
      navigate("/", { replace: true });
    }
  }, [isMinorUser, location.pathname, navigate]);
  // Masque aussi les liens sociaux pour les mineurs (sidebar + menu), pas seulement la redirection.
  const _social = ["/community", "/espace-communautaire", "/idees", "/membre"];
  const navItems = isMinorUser ? NAV.filter((n) => !_social.some((s) => n.to.startsWith(s))) : NAV;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(() => {
    try { return !sessionStorage.getItem("bizarre_intro_v1"); } catch { return false; }
  });
  const dismissIntro = () => { try { sessionStorage.setItem("bizarre_intro_v1", "1"); } catch {} setShowIntro(false); };

  const NavItem = ({ to, k, icon, emoji }) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
          active ? "bg-white/[0.06] text-white" : "text-white/50 hover:text-white hover:bg-white/[0.03]"
        }`}
      >
        <Glyph icon={icon} emoji={emoji} active={active} />
        <span>{t(k)}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-white/10 p-5 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 mb-1 px-1">
          <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center">
            <span className="text-black font-bold text-sm font-display">B</span>
          </div>
          <span className="font-display text-lg tracking-tight">BIZARRE <span className="gold-text">AI</span></span>
        </Link>
        <p className="text-white/25 text-[10px] uppercase tracking-[0.25em] px-1 mb-7">{t("brand.home")}</p>
        <nav className="flex flex-col gap-1 flex-1">
          {navItems.map((n) => <NavItem key={n.to} {...n} />)}
          {user?.role === "admin" && (
            <NavItem to="/admin" k="common.admin" icon={Shield} />
          )}
        </nav>
        <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
          {!user?.is_premium && (
            <Link to="/premium" data-testid="nav-premium"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm bg-gradient-to-r from-[#BF953F]/20 to-[#B38728]/10 border border-gold/30 text-gold">
              <Crown size={18} strokeWidth={1.5} /> {t("common.goPremium")}
            </Link>
          )}
          <Link to="/profile" data-testid="nav-profile"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/[0.03]">
            <User size={18} strokeWidth={1.5} /> {user?.name || t("common.profile")}
          </Link>
          <div className="px-3 py-1 flex items-center justify-between">
            <NotificationBell />
            <LanguageToggle />
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/[0.03]">
            <LogOut size={18} strokeWidth={1.5} /> {t("common.logout")}
          </button>
          <p className="text-white/20 text-[11px] px-3 pt-2">{t("brand.version")}</p>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass border-b border-white/10 flex items-center justify-between px-4 h-14">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
            <span className="text-black font-bold text-xs font-display">B</span>
          </div>
          <span className="font-display tracking-tight">BIZARRE <span className="gold-text">AI</span></span>
        </Link>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="mobile-admin" className="text-gold">
              <Shield size={20} />
            </Link>
          )}
          <NotificationBell />
          <LanguageToggle />
          <Link to="/profile" data-testid="mobile-profile" className="text-white/60"><User size={20} /></Link>
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" className="text-white/70">
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Mobile full-menu drawer */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-[1200]" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 max-w-[85%] bg-[#0A0A0A] border-l border-white/10 p-5 flex flex-col animate-in slide-in-from-right duration-200 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <span className="font-display text-lg tracking-tight">{t("brand.menu")}</span>
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="text-white/60 hover:text-white"><X size={22} /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {navItems.map(({ to, k, icon, emoji }) => (
                <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                  data-testid={`drawer-nav-${to === "/" ? "home" : to.slice(1)}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${location.pathname === to ? "bg-white/[0.06] text-white" : "text-white/60 hover:text-white hover:bg-white/[0.03]"}`}>
                  <Glyph icon={icon} emoji={emoji} active={location.pathname === to} /> {t(k)}
                </Link>
              ))}
              {user?.role === "admin" && (
                <Link to="/admin" onClick={() => setMenuOpen(false)} data-testid="drawer-nav-admin"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${location.pathname === "/admin" ? "bg-white/[0.06] text-white" : "text-gold hover:bg-white/[0.03]"}`}>
                  <Shield size={18} strokeWidth={1.5} /> {t("common.admin")}
                </Link>
              )}
            </nav>
            <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
              {!user?.is_premium && (
                <Link to="/premium" onClick={() => setMenuOpen(false)} data-testid="drawer-nav-premium"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm bg-gradient-to-r from-[#BF953F]/20 to-[#B38728]/10 border border-gold/30 text-gold">
                  <Crown size={18} strokeWidth={1.5} /> {t("common.goPremium")}
                </Link>
              )}
              <Link to="/profile" onClick={() => setMenuOpen(false)} data-testid="drawer-nav-profile"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/[0.03]">
                <User size={18} strokeWidth={1.5} /> {user?.name || t("common.profile")}
              </Link>
              <button onClick={() => { setMenuOpen(false); logout(); navigate("/login"); }} data-testid="drawer-logout-button"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/[0.03]">
                <LogOut size={18} strokeWidth={1.5} /> {t("common.logout")}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0 pb-24 lg:pb-0">
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      <ConfidentialNotice />
      <ProfilePicker />
      {showIntro && <BrandIntro onDone={dismissIntro} />}

      {/* Mobile bottom nav */}
      {themed ? (
        <ProfileBottomNav profile={profile} location={location} />
      ) : (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10 grid grid-cols-6 px-1 py-1.5">
          {NAV.slice(0, 6).map(({ to, k, icon: Icon }) => {
            const active = location.pathname === to;
            return (
              <Link key={to} to={to} data-testid={`mnav-${to === "/" ? "home" : to.slice(1)}`}
                className={`flex flex-col items-center gap-0.5 py-1 text-[10px] ${active ? "text-gold" : "text-white/50"}`}>
                <Icon size={20} strokeWidth={1.5} /> {t(k)}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}
